// context/SessionContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import ApiConfig from '../api/config';   // ✅ Direct import — no dynamic import needed

// ============================================================
// Session storage manager (singleton)
// ============================================================
class SessionManager {
  constructor() {
    this._token = null;
    this._refreshToken = null;
    this._userName = null;
    this._expirySecs = null;
    this._sessionId = null;
    this._tokenExpiryTime = null;

    this._loadFromStorage();
  }

  _loadFromStorage() {
    try {
      const stored = sessionStorage.getItem('paynuts_session');
      if (stored) {
        const data = JSON.parse(stored);
        this._token = data.token;
        this._refreshToken = data.refreshToken;
        this._userName = data.userName;
        this._expirySecs = data.expirySecs;
        this._sessionId = data.sessionId;
        this._tokenExpiryTime = data.tokenExpiryTime;
        // Do NOT clear session here — axios will refresh on 401.
      }
    } catch (e) {
      console.error('Error loading session from storage:', e);
      this.clearSession();
    }
  }

  _saveToStorage() {
    try {
      const data = {
        token: this._token,
        refreshToken: this._refreshToken,
        userName: this._userName,
        expirySecs: this._expirySecs,
        sessionId: this._sessionId,
        tokenExpiryTime: this._tokenExpiryTime,
      };
      sessionStorage.setItem('paynuts_session', JSON.stringify(data));
    } catch (e) {
      console.error('Error saving session to storage:', e);
    }
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  setSession(token, refreshToken, userName, expirySecs) {
    this._token = token;
    this._refreshToken = refreshToken;
    this._userName = userName;
    this._expirySecs = expirySecs || 900;
    if (!this._sessionId) {
      this._sessionId = this.generateSessionId();
    }
    this._tokenExpiryTime = Date.now() + this._expirySecs * 1000;

    this._saveToStorage();

    document.cookie = `sessionId=${this._sessionId}; path=/; secure; SameSite=Strict; max-age=${this._expirySecs}`;
  }

  getSession() {
    return {
      token: this._token,
      refreshToken: this._refreshToken,
      userName: this._userName,
      expirySecs: this._expirySecs,
      sessionId: this._sessionId,
      tokenExpiryTime: this._tokenExpiryTime,
    };
  }

  isTokenExpired() {
    if (!this._tokenExpiryTime) return true;
    return Date.now() >= this._tokenExpiryTime;
  }

  isAuthenticated() {
    if (!this._sessionId) return false;
    if (this._token && !this.isTokenExpired()) return true;
    if (this._refreshToken) return true;
    return false;
  }

  clearSession() {
    this._token = null;
    this._refreshToken = null;
    this._userName = null;
    this._expirySecs = null;
    this._sessionId = null;
    this._tokenExpiryTime = null;

    try {
      sessionStorage.removeItem('paynuts_session');
    } catch (e) {
      // ignore
    }

    document.cookie =
      'sessionId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }

  getSessionIdFromCookie() {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'sessionId') return value;
    }
    return null;
  }

  async validateSession() {
    const sessionId = this.getSessionIdFromCookie();
    if (!sessionId || sessionId !== this._sessionId) return false;
    return this.isAuthenticated();
  }
}

const sessionManager = new SessionManager();

// ============================================================
// Context
// ============================================================
const SessionContext = createContext(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

// ============================================================
// Provider
// ============================================================
export const SessionProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const initializedRef = useRef(false);

  // ----------------------------------------------------------
  // 🔄 REFRESH TOKEN — calls PUT /RefreshToken
  // (Not called automatically — axios interceptor handles that)
  // ----------------------------------------------------------
  const refreshToken = useCallback(async () => {
    const currentSession = sessionManager.getSession();

    if (!currentSession.refreshToken) {
      console.warn('[refreshToken] No refresh token available');
      return { success: false, error: 'No refresh token' };
    }

    try {
      console.log('[refreshToken] Calling:', ApiConfig.ENDPOINTS.REFRESH_TOKEN);

      const response = await fetch(ApiConfig.ENDPOINTS.REFRESH_TOKEN, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: currentSession.refreshToken }),
      });

      if (response.status === 401 || response.status === 403) {
        console.warn('[refreshToken] Refresh token rejected — logging out');
        sessionManager.clearSession();
        setSession(null);
        setIsAuthenticated(false);
        return { success: false, error: 'Refresh rejected' };
      }

      const data = await response.json();

      if (response.ok && data.token) {
        sessionManager.setSession(
          data.token,
          data.refreshToken || currentSession.refreshToken,
          currentSession.userName,
          data.expirySecs || currentSession.expirySecs || 900
        );

        const newSession = sessionManager.getSession();
        setSession(newSession);
        setIsAuthenticated(true);

        return { success: true, token: data.token };
      }

      return { success: false, error: 'Refresh returned no token' };
    } catch (error) {
      console.warn('[refreshToken] Network/parse error:', error);
      return { success: false, error };
    }
  }, []);

  // ----------------------------------------------------------
  // Sync from sessionManager (called by axios after refresh)
  // ----------------------------------------------------------
  const syncFromStorage = useCallback(() => {
    const fresh = sessionManager.getSession();
    if (fresh?.token) {
      setSession(fresh);
      setIsAuthenticated(true);
    }
  }, []);

  // ----------------------------------------------------------
  // Initialize on mount — hydrate from sessionStorage only
  // ----------------------------------------------------------
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    try {
      const currentSession = sessionManager.getSession();

      if (currentSession.token || currentSession.refreshToken) {
        setSession(currentSession);
        setIsAuthenticated(true);
      } else {
        setSession(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('[initSession] Session init error:', error);
      sessionManager.clearSession();
      setSession(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // ----------------------------------------------------------
  // 🔐 LOGIN — calls POST /LoginUser
  // ----------------------------------------------------------
  const login = useCallback(async (username, password) => {
    try {
      console.log('[login] Calling:', ApiConfig.ENDPOINTS.LOGIN);

      const response = await fetch(ApiConfig.ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.token) {
        sessionManager.setSession(
          data.token,
          data.refreshToken,
          data.name || username,
          data.expirySecs || 900
        );

        const newSession = sessionManager.getSession();
        setSession(newSession);
        setIsAuthenticated(true);

        console.log('[login] Success — session established');

        return {
          success: true,
          user: { name: data.name || username },
        };
      }

      return {
        success: false,
        message: data.message || 'Login failed',
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Login failed. Please try again.',
      };
    }
  }, []);

  // ----------------------------------------------------------
  // 🚪 LOGOUT — calls PUT /LogoutUser
  // ----------------------------------------------------------
  const logout = useCallback(async () => {
    try {
      const currentSession = sessionManager.getSession();

      if (currentSession.refreshToken) {
        console.log('[logout] Calling:', ApiConfig.ENDPOINTS.LOGOUT);

        await fetch(ApiConfig.ENDPOINTS.LOGOUT, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${currentSession.token}`,
          },
          body: JSON.stringify({
            refreshToken: currentSession.refreshToken,
          }),
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionManager.clearSession();
      setSession(null);
      setIsAuthenticated(false);
    }
  }, []);

  const getSession = useCallback(() => sessionManager.getSession(), []);
  const checkAuth = useCallback(() => sessionManager.isAuthenticated(), []);

  const value = {
    session,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshToken,
    getSession,
    checkAuth,
    syncFromStorage,
    sessionManager,
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};